import React, { useState, useEffect } from 'react';
import { Route, Switch, Redirect } from "react-router-dom";
import { Routes } from "../routes";
import { getAuthUser } from "../api/tokenStorage";

// pages
import Presentation from "./Presentation";
import Upgrade from "./Upgrade";
import DashboardOverview from "./dashboard/DashboardOverview";
import Transactions from "./Transactions";
import Settings from "./Settings";
import Vendors from "./Vendors";
import Orders from "./Orders";
import OrderDetails from "./OrderDetails";
import Products from "./Products";
import ProductDetails from "./ProductDetails";
import ProductForm from "./ProductForm";
import VendorProductDetails from "./VendorProductDetails";
import VendorProducts from "./VendorProducts";
import VendorCart from "./VendorCart";
import VendorOrders from "./VendorOrders";
import VendorOrderDetails from "./VendorOrderDetails";
import VendorRegister from "./VendorRegister";
import BootstrapTables from "./tables/BootstrapTables";
import Signin from "./examples/Signin";
import Signup from "./examples/Signup";
import ForgotPassword from "./examples/ForgotPassword";
import ResetPassword from "./examples/ResetPassword";
import Lock from "./examples/Lock";
import NotFoundPage from "./examples/NotFound";
import ServerError from "./examples/ServerError";

// documentation pages
import DocsOverview from "./documentation/DocsOverview";
import DocsDownload from "./documentation/DocsDownload";
import DocsQuickStart from "./documentation/DocsQuickStart";
import DocsLicense from "./documentation/DocsLicense";
import DocsFolderStructure from "./documentation/DocsFolderStructure";
import DocsBuild from "./documentation/DocsBuild";
import DocsChangelog from "./documentation/DocsChangelog";

// components
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import VendorNavbar from "../components/VendorNavbar";
import VendorBottomNavbar from "../components/VendorBottomNavbar";
import Footer from "../components/Footer";
import Preloader from "../components/Preloader";

import Accordion from "./components/Accordion";
import Alerts from "./components/Alerts";
import Badges from "./components/Badges";
import Breadcrumbs from "./components/Breadcrumbs";
import Buttons from "./components/Buttons";
import Forms from "./components/Forms";
import Modals from "./components/Modals";
import Navs from "./components/Navs";
import Navbars from "./components/Navbars";
import Pagination from "./components/Pagination";
import Popovers from "./components/Popovers";
import Progress from "./components/Progress";
import Tables from "./components/Tables";
import Tabs from "./components/Tabs";
import Tooltips from "./components/Tooltips";
import Toasts from "./components/Toasts";

const getCurrentUser = () => getAuthUser();
const isVendorUser = () => Number(getCurrentUser()?.role_id) === 2;
const defaultAuthenticatedPath = () => (isVendorUser() ? Routes.Products.path : Routes.DashboardOverview.path);

const SidebarLayout = ({ Component, routeProps }) => {
  const localStorageIsSettingsVisible = () => {
    return localStorage.getItem('settingsVisible') === 'false' ? false : true;
  };

  const [showSettings, setShowSettings] = useState(localStorageIsSettingsVisible);

  const toggleSettings = () => {
    setShowSettings(!showSettings);
    localStorage.setItem('settingsVisible', !showSettings);
  };

  return (
    <>
      <Sidebar />

      <main className="content">
        <Component {...routeProps} />
        <Footer toggleSettings={toggleSettings} showSettings={showSettings} />
      </main>
    </>
  );
};

const VendorLayout = ({ Component, routeProps }) => {
  return (
    <>
      <VendorNavbar />
      <main className="py-4 py-lg-3 pb-vendor-nav vendor-page-container">
        <div className="container-fluid px-0">
          <Component {...routeProps} />
        </div>
      </main>
      <VendorBottomNavbar />
    </>
  );
};

const RouteWithLoader = ({ component: Component, ...rest }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Route {...rest} render={props => ( <> <Preloader show={loaded ? false : true} /> <Component {...props} /> </> ) } />
  );
};

const RouteWithSidebar = ({ component: Component, ...rest }) => {
  return (
    <Route
      {...rest}
      render={props => <SidebarLayout Component={Component} routeProps={props} />}
    />
  );
};

const RouteWithVendorShell = ({ component: Component, ...rest }) => {
  return (
    <Route
      {...rest}
      render={props => {
        const user = getCurrentUser();

        if (!user) {
          return <Redirect to={Routes.Signin.path} />;
        }

        if (!isVendorUser()) {
          return <Redirect to={defaultAuthenticatedPath()} />;
        }

        return <VendorLayout Component={Component} routeProps={props} />;
      }}
    />
  );
};

export default () => (
  <Switch>
    <Redirect exact from={Routes.Home.path} to={getCurrentUser() ? defaultAuthenticatedPath() : Routes.Signin.path} />
    <RouteWithLoader exact path={Routes.Presentation.path} component={Presentation} />
    <RouteWithLoader exact path={Routes.Signin.path} component={Signin} />
    <RouteWithLoader exact path={Routes.VendorRegister.path} component={VendorRegister} />
    <RouteWithLoader exact path={Routes.Signup.path} component={Signup} />
    <RouteWithLoader exact path={Routes.ForgotPassword.path} component={ForgotPassword} />
    <RouteWithLoader exact path={Routes.ResetPassword.path} component={ResetPassword} />
    <RouteWithLoader exact path={Routes.Lock.path} component={Lock} />
    <RouteWithLoader exact path={Routes.NotFound.path} component={NotFoundPage} />
    <RouteWithLoader exact path={Routes.ServerError.path} component={ServerError} />

    {/* pages */}
    <Route exact path={Routes.DashboardOverview.path} render={(props) => (
      !getCurrentUser()
        ? <Redirect to={Routes.Signin.path} />
        : isVendorUser()
          ? <Redirect to={Routes.Products.path} />
          : <SidebarLayout Component={DashboardOverview} routeProps={props} />
    )} />
    <RouteWithSidebar exact path={Routes.Vendors.path} component={Vendors} />
    <RouteWithSidebar exact path={Routes.Orders.path} component={Orders} />
    <RouteWithSidebar exact path={Routes.OrderDetails.path} component={OrderDetails} />
    <Route exact path={Routes.Products.path} render={(props) => (
      !getCurrentUser()
        ? <Redirect to={Routes.Signin.path} />
        : isVendorUser()
          ? <VendorLayout Component={VendorProducts} routeProps={props} />
          : <SidebarLayout Component={Products} routeProps={props} />
    )} />
    <RouteWithVendorShell exact path={Routes.Cart.path} component={VendorCart} />
    <RouteWithVendorShell exact path={Routes.MyOrders.path} component={VendorOrders} />
    <RouteWithVendorShell exact path={Routes.MyOrderDetails.path} component={VendorOrderDetails} />
    <RouteWithSidebar exact path={Routes.ProductCreate.path} component={() => <ProductForm mode="create" />} />
    <RouteWithSidebar exact path={`${Routes.Products.path}/:id/edit`} component={() => <ProductForm mode="edit" />} />
    <Route exact path={`${Routes.Products.path}/:id`} render={(props) => (
      !getCurrentUser()
        ? <Redirect to={Routes.Signin.path} />
        : isVendorUser()
          ? <VendorLayout Component={VendorProductDetails} routeProps={props} />
          : <SidebarLayout Component={ProductDetails} routeProps={props} />
    )} />
    <RouteWithSidebar exact path={Routes.Upgrade.path} component={Upgrade} />
    <RouteWithSidebar exact path={Routes.Transactions.path} component={Transactions} />
    <RouteWithSidebar exact path={Routes.Settings.path} component={Settings} />
    <RouteWithSidebar exact path={Routes.BootstrapTables.path} component={BootstrapTables} />

    {/* components */}
    <RouteWithSidebar exact path={Routes.Accordions.path} component={Accordion} />
    <RouteWithSidebar exact path={Routes.Alerts.path} component={Alerts} />
    <RouteWithSidebar exact path={Routes.Badges.path} component={Badges} />
    <RouteWithSidebar exact path={Routes.Breadcrumbs.path} component={Breadcrumbs} />
    <RouteWithSidebar exact path={Routes.Buttons.path} component={Buttons} />
    <RouteWithSidebar exact path={Routes.Forms.path} component={Forms} />
    <RouteWithSidebar exact path={Routes.Modals.path} component={Modals} />
    <RouteWithSidebar exact path={Routes.Navs.path} component={Navs} />
    <RouteWithSidebar exact path={Routes.Navbars.path} component={Navbars} />
    <RouteWithSidebar exact path={Routes.Pagination.path} component={Pagination} />
    <RouteWithSidebar exact path={Routes.Popovers.path} component={Popovers} />
    <RouteWithSidebar exact path={Routes.Progress.path} component={Progress} />
    <RouteWithSidebar exact path={Routes.Tables.path} component={Tables} />
    <RouteWithSidebar exact path={Routes.Tabs.path} component={Tabs} />
    <RouteWithSidebar exact path={Routes.Tooltips.path} component={Tooltips} />
    <RouteWithSidebar exact path={Routes.Toasts.path} component={Toasts} />

    {/* documentation */}
    <RouteWithSidebar exact path={Routes.DocsOverview.path} component={DocsOverview} />
    <RouteWithSidebar exact path={Routes.DocsDownload.path} component={DocsDownload} />
    <RouteWithSidebar exact path={Routes.DocsQuickStart.path} component={DocsQuickStart} />
    <RouteWithSidebar exact path={Routes.DocsLicense.path} component={DocsLicense} />
    <RouteWithSidebar exact path={Routes.DocsFolderStructure.path} component={DocsFolderStructure} />
    <RouteWithSidebar exact path={Routes.DocsBuild.path} component={DocsBuild} />
    <RouteWithSidebar exact path={Routes.DocsChangelog.path} component={DocsChangelog} />

    <Redirect to={Routes.NotFound.path} />
  </Switch>
);
